<template>
  <div class="forum_group">
    <div class="forum_group__cover" :style="{ backgroundImage: detail.background ? `url(${detail.background})` : '' }">
      <button type="button" class="forum_group__message" @click="goMessages">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 5.5A3.5 3.5 0 0 1 7.5 2h9A3.5 3.5 0 0 1 20 5.5v7A3.5 3.5 0 0 1 16.5 16H10l-4.2 3.15A1 1 0 0 1 4 18.35V5.5z" />
        </svg>
        <span>消息</span>
        <em v-if="hasUnreadMessage" />
      </button>
      <div class="forum_group__info">
        <div class="forum_group__icon">
          <img v-if="detail.groupIcon" :src="detail.groupIcon" alt="" />
        </div>
        <div class="forum_group__text">
          <div class="forum_group__name text_ellipsis">{{ detail.groupName }}</div>
          <div class="forum_group__desc text_ellipsis">{{ groupDescription }}</div>
          <div v-if="boundAccountName" class="forum_group__bind_account text_ellipsis">已绑定账号：{{ boundAccountName }}</div>
        </div>
      </div>
    </div>

    <div v-if="navList.length || topList.length" class="forum_group__top_block">
      <div v-if="navList.length" class="forum_group__nav">
        <button v-for="(nav, index) in navList" :key="nav.navigationId || index" type="button" class="forum_group__nav_item" @click="onNavClick(nav)">
          <div class="forum_group__nav_icon">
            <img v-if="nav.navigationIcon || nav.icon || nav.image" :src="nav.navigationIcon || nav.icon || nav.image" alt="" />
          </div>
          <span class="text_ellipsis">{{ getNavLabel(nav) }}</span>
        </button>
      </div>
      <TopPostList :list="topList" @post-click="onTopPostClick" @toggle="onTopPostToggle" />
    </div>

    <div class="forum_group__filter">
      <div class="forum_group__primary">
        <div class="forum_group__primary_tabs">
          <button
            v-for="tag in tags"
            :key="tag.tagId"
            type="button"
            :class="['forum_group__primary_item', { active: String(activeTagId) === String(tag.tagId) }]"
            @click="onPrimaryChange(tag)"
          >
            <span>{{ tag.tagName }}</span>
            <i />
          </button>
        </div>
        <button type="button" class="forum_group__search" @click="goSearch">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="M16.5 16.5L21 21" />
          </svg>
          <span>搜索</span>
        </button>
      </div>
      <div v-if="subTagOptions.length" class="forum_group__secondary">
        <div class="forum_group__secondary_tabs">
          <button
            v-for="tag in subTagOptions"
            :key="tag.tagId"
            type="button"
            :class="['forum_group__secondary_item', { active: String(activeSubTagId) === String(tag.tagId) }]"
            @click="onSubChange(tag)"
          >{{ tag.tagName }}</button>
        </div>
        <div class="forum_group__sort">
          <button :class="{ active: sortType === 1 }" @click="onSortChange(1)">最新</button>
          <span>/</span>
          <button :class="{ active: sortType === 2 }" @click="onSortChange(2)">最热</button>
        </div>
      </div>
    </div>

    <van-list
      v-model="listLoading"
      :finished="finished"
      :immediate-check="false"
      :finished-text="postListFinishedText"
      @load="loadPosts(false)"
    >
      <PostItem
        v-for="post in postList"
        :key="post.postsId"
        :data="post"
        @click="onPostClick"
        @comment="onComment"
        @praise="onPraise"
        @collect="onCollect"
      />
    </van-list>
    <EmptyState v-if="!listLoading && postList.length === 0" text="暂无内容，快来发第一帖吧" />

    <button type="button" class="forum_group__create" @click="goCreate">＋</button>
  </div>
</template>

<script>
import { List } from 'vant';
import { mapState } from 'vuex';
import {
  collectOrCancelPosts,
  getCommunityUnreadMsgCount,
  getGroupDetail,
  getNavigationList,
  getPostList,
  getPostTopList,
  getTagList,
  praiseOrCancelPosts,
} from '@/api/forum';
import { toastInteraction, isInteractionOk } from '@/utils/forum/forumInteraction';
import { takeForumGroupReturnAction } from '@/utils/forum/groupReturnAction';
import { extractForumRecords } from '@/utils/forum/postList';
import { applyForumPostInteractionPatches } from '@/utils/forum/postInteractionCache';
import EmptyState from './components/EmptyState.vue';
import PostItem from './components/PostItem.vue';
import TopPostList from './components/TopPostList.vue';
import trackingMixin, { EVENT_TYPE_ENUM } from './mixins/tracking';
import forumLoginRefreshMixin from './mixins/forumLoginRefresh';

const POST_LIST_PAGE_SIZE = 18;
const GROUP_RETURN_STATE_STORAGE_KEY = 'FORUM_GROUP_RETURN_STATE';
const GROUP_DETAIL_BACK_CODE = '7068';
const GROUP_DETAIL_OFFLINE_CODE = '7038';
const GROUP_DETAIL_BACK_CODES = [GROUP_DETAIL_BACK_CODE, GROUP_DETAIL_OFFLINE_CODE];

export default {
  name: 'ForumGroupHome',
  mixins: [trackingMixin, forumLoginRefreshMixin],
  components: { [List.name]: List, EmptyState, PostItem, TopPostList },
  data() {
    return {
      detail: {},
      navList: [],
      tags: [],
      activeTagId: '',
      activeSubTagId: '',
      subTagOptions: [],
      sortType: 1,
      topList: [],
      postList: [],
      listLoading: false,
      listRequesting: false,
      finished: false,
      lastPostId: 0,
      bootstrappedGroupId: '',
      bootstrapping: false,
      skipNextActivationReload: false,
      savedScrollTop: 0,
      unreadCount: 0,
    };
  },
  computed: {
    ...mapState('account', {
      accountUserInfo: (state) => state.userInfo || {},
    }),
    groupId() {
      return String(this.$route.params.groupId || '');
    },
    groupDescription() {
      return String(this.detail.description || '')
        .replace(/[\r\n]+/g, ' ')
        .trim();
    },
    postListFinishedText() {
      return Array.isArray(this.postList) && this.postList.length > 0 ? '没有更多了' : '';
    },
    hasUnreadMessage() {
      return Number(this.unreadCount || 0) > 0;
    },
    boundAccountName() {
      const info = this.accountUserInfo || {};
      return info.game_account || '';
    },
  },
  activated() {
    if (this.bootstrapping) return;
    const sameGroup = String(this.bootstrappedGroupId) === String(this.groupId);
    const returnState = this.takeGroupReturnState();
    const returnAction = takeForumGroupReturnAction(this.groupId);
    const shouldResetAfterCreate = Boolean(returnAction && returnAction.postCreated);
    const deletedPostIds = returnAction && returnAction.deletedPostIds ? returnAction.deletedPostIds : [];
    const hasDeletedPostAction = deletedPostIds.length > 0;
    const hasReusableList = this.postList.length || (hasDeletedPostAction && this.topList.length);
    if (shouldResetAfterCreate) {
      this.skipNextActivationReload = false;
      this.bootstrap({ resetTagToFirst: true }).then(() => this.restoreScroll(0));
      return;
    }
    if ((this.skipNextActivationReload || returnState) && sameGroup && hasReusableList) {
      this.skipNextActivationReload = false;
      this.applyCachedPostInteractions();
      this.removeDeletedPosts(deletedPostIds);
      this.fetchUnreadCount();
      const scrollTop = returnState && returnState.scrollTop !== undefined ? returnState.scrollTop : this.savedScrollTop;
      this.restoreScroll(scrollTop);
      return;
    }
    this.skipNextActivationReload = false;
    this.bootstrap();
  },
  created() {
    const returnAction = takeForumGroupReturnAction(this.groupId);
    const shouldResetAfterCreate = Boolean(returnAction && returnAction.postCreated);
    const deletedPostIds = returnAction && returnAction.deletedPostIds ? returnAction.deletedPostIds : [];
    this.bootstrap({ resetTagToFirst: shouldResetAfterCreate }).then(() => {
      this.removeDeletedPosts(deletedPostIds);
      if (shouldResetAfterCreate) this.restoreScroll(0);
    });
  },
  beforeRouteLeave(to, from, next) {
    this.skipNextActivationReload = to.name === 'forum-post-detail';
    if (this.skipNextActivationReload) this.saveGroupReturnState();
    next();
  },
  methods: {
    getForumLoginRefreshConfig() {
      return {
        routeName: 'forum-group',
        when: () => Boolean(this.groupId),
        onRefresh: () => this.bootstrap(),
      };
    },
    getGroupReturnStore() {
      try {
        return JSON.parse(sessionStorage.getItem(GROUP_RETURN_STATE_STORAGE_KEY) || '{}') || {};
      } catch (error) {
        return {};
      }
    },
    getScrollTop() {
      return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    },
    saveGroupReturnState() {
      if (!this.groupId) return;
      this.savedScrollTop = this.getScrollTop();
      try {
        const store = this.getGroupReturnStore();
        store[String(this.groupId)] = { scrollTop: this.savedScrollTop };
        sessionStorage.setItem(GROUP_RETURN_STATE_STORAGE_KEY, JSON.stringify(store));
      } catch (error) {
        // 缓存失败不影响返回列表。
      }
    },
    takeGroupReturnState() {
      if (!this.groupId) return null;
      try {
        const store = this.getGroupReturnStore();
        const state = store[String(this.groupId)] || null;
        delete store[String(this.groupId)];
        sessionStorage.setItem(GROUP_RETURN_STATE_STORAGE_KEY, JSON.stringify(store));
        return state;
      } catch (error) {
        return null;
      }
    },
    restoreScroll(scrollTop) {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          window.scrollTo(0, Number(scrollTop) || 0);
        });
      });
    },
    getApiErrorCode(error) {
      if (!error || error.code === undefined || error.code === null) return '';
      return String(error.code);
    },
    backOnGroupDetailError(error) {
      if (GROUP_DETAIL_BACK_CODES.indexOf(this.getApiErrorCode(error)) === -1) return false;
      this.$router.replace({ name: 'forum-square' });
      return true;
    },
    getGroupTrackingFields() {
      return {
        page_type: '论坛首页',
        group_id: this.groupId,
        group_name: this.detail.groupName || '',
      };
    },
    getUnreadCount(data) {
      if (data && typeof data === 'object') {
        return Number(data.count || data.total || data.unreadCount || data.num || 0);
      }
      return Number(data || 0);
    },
    async fetchUnreadCount() {
      const res = await getCommunityUnreadMsgCount();
      this.unreadCount = this.getUnreadCount(res && res.data);
    },
    getPostTrackingFields(post, location) {
      return {
        ...this.getGroupTrackingFields(),
        ...(location ? { location } : {}),
        post_id: post.postsId || post.postId || '',
        post_title: post.title || '',
      };
    },
    getHomeTagTrackingFields(primaryTag, secondTagId = this.activeSubTagId) {
      const currentPrimaryTag = primaryTag || this.tags.find((item) => String(item.tagId) === String(this.activeTagId)) || {};
      const secondTag = this.subTagOptions.find((item) => String(item.tagId) === String(secondTagId)) || {};
      return {
        ...this.getGroupTrackingFields(),
        primary_tag_id: currentPrimaryTag.tagId || '',
        primary_tag_name: currentPrimaryTag.tagName || '',
        second_tag_id: secondTag.tagId || secondTagId || '',
        second_tag_name: secondTag.tagName || '',
      };
    },
    trackHomeTagClick(primaryTag) {
      const currentPrimaryTag = primaryTag || this.tags.find((item) => String(item.tagId) === String(this.activeTagId));
      if (!currentPrimaryTag) return;
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_home_tag_click, {
        ...this.getHomeTagTrackingFields(currentPrimaryTag),
      });
    },
    trackHomeButton(buttonType) {
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_home_button, {
        ...this.getGroupTrackingFields(),
        button_type: buttonType,
      });
    },
    trackPostOpt(post, buttonType) {
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_home_post_opt_click, {
        ...this.getPostTrackingFields(post),
        button_type: buttonType,
      });
    },
    markPostDetailReturn() {
      this.skipNextActivationReload = true;
      this.saveGroupReturnState();
    },
    applyCachedPostInteractions() {
      const applied = applyForumPostInteractionPatches([this.postList, this.topList]);
      if (!applied) return;
      this.postList = this.postList.slice();
      this.topList = this.topList.slice();
    },
    getListPostId(post) {
      if (!post) return '';
      if (post.postsId !== undefined && post.postsId !== null) return String(post.postsId);
      if (post.postId !== undefined && post.postId !== null) return String(post.postId);
      return '';
    },
    removeDeletedPosts(postIds) {
      if (!Array.isArray(postIds) || postIds.length === 0) return;
      const deletedSet = new Set(postIds.map((id) => String(id)));
      this.postList = this.postList.filter((post) => !deletedSet.has(this.getListPostId(post)));
      this.topList = this.topList.filter((post) => !deletedSet.has(this.getListPostId(post)));
    },
    async bootstrap(options = {}) {
      if (!this.groupId) return;
      if (this.bootstrapping) return;
      this.bootstrapping = true;
      this.bootstrappedGroupId = this.groupId;
      try {
        let detailRes = null;
        try {
          detailRes = await getGroupDetail(this.groupId);
        } catch (error) {
          if (this.backOnGroupDetailError(error)) return;
        }
        const [navRes, tagRes, topRes, unreadRes] = await Promise.all([
          getNavigationList(this.groupId),
          getTagList(this.groupId),
          getPostTopList(this.groupId),
          getCommunityUnreadMsgCount(),
        ]);
        this.detail = (detailRes && detailRes.data) || {};
        this.navList = (navRes && navRes.data) || [];
        this.tags = (tagRes && tagRes.data) || [];
        this.topList = extractForumRecords(topRes && topRes.data);
        this.unreadCount = this.getUnreadCount(unreadRes && unreadRes.data);
        this.applyCachedPostInteractions();
        this.applyDefaultTag(options);
        this.loadPosts(true);
        this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_square_page_show, {
          ...this.getGroupTrackingFields(),
        });
        this.trackHomeTagClick();
      } finally {
        this.bootstrapping = false;
      }
    },
    applyDefaultTag(options = {}) {
      const current = options.resetTagToFirst
        ? this.tags[0]
        : this.tags.find((item) => String(item.tagId) === String(this.activeTagId)) || this.tags[0];
      if (!current) return;
      this.activeTagId = current.tagId;
      this.subTagOptions = current.secondTagList || [];
      const allTag = this.subTagOptions.find((item) => item.allTag === 1);
      const firstSubTag = options.resetTagToFirst ? this.subTagOptions[0] : (allTag || this.subTagOptions[0]);
      this.activeSubTagId = (firstSubTag || {}).tagId || '';
    },
    onPrimaryChange(tag) {
      this.activeTagId = tag.tagId;
      this.subTagOptions = tag.secondTagList || [];
      const allTag = this.subTagOptions.find((item) => item.allTag === 1);
      this.activeSubTagId = (allTag || this.subTagOptions[0] || {}).tagId || '';
      this.trackHomeTagClick(tag);
      this.loadPosts(true);
    },
    onSubChange(tag) {
      const primaryTag = this.tags.find((item) => String(item.tagId) === String(this.activeTagId)) || {};
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_home_tag_click, {
        ...this.getHomeTagTrackingFields(primaryTag, tag.tagId),
      });
      this.activeSubTagId = tag.tagId;
      this.loadPosts(true);
    },
    onSortChange(type) {
      if (this.sortType === type) return;
      this.trackHomeButton(type === 1 ? '最新' : '最热');
      this.sortType = type;
      this.loadPosts(true);
    },
    async loadPosts(reset = false) {
      if (this.listRequesting) return;
      if (reset) {
        this.postList = [];
        this.lastPostId = 0;
        this.finished = false;
      }
      if (this.finished) {
        this.listLoading = false;
        return;
      }
      if (!this.groupId || this.activeSubTagId === undefined || this.activeSubTagId === null || String(this.activeSubTagId).trim() === '') {
        this.finished = true;
        this.listLoading = false;
        return;
      }
      this.listRequesting = true;
      this.listLoading = true;
      try {
        const res = await getPostList({
          groupId: this.groupId,
          tagId: this.activeSubTagId,
          sortType: this.sortType,
          lastPostId: this.lastPostId,
        });
        const records = extractForumRecords(res && res.data);
        if (!records.length) {
          this.finished = true;
        } else {
          this.postList.push(...records);
          this.lastPostId = records[records.length - 1].postsId;
          this.applyCachedPostInteractions();
          if (records.length < POST_LIST_PAGE_SIZE) this.finished = true;
        }
      } catch (error) {
        this.finished = true;
      } finally {
        this.listRequesting = false;
        this.listLoading = false;
      }
    },
    /** 导航展示文案：优先 tag，否则 navigationName */
    getNavLabel(nav) {
      const tagRaw = nav && nav.tag;
      if (tagRaw !== undefined && tagRaw !== null && String(tagRaw).trim() !== '') return String(tagRaw).trim();
      const nameRaw = nav && nav.navigationName;
      if (nameRaw !== undefined && nameRaw !== null && String(nameRaw).trim() !== '') return String(nameRaw).trim();
      return (nav && nav.tagName) || '未命名';
    },
    onNavClick(nav) {
      const navLabel = this.getNavLabel(nav);
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_home_navigation_click, {
        ...this.getGroupTrackingFields(),
        navigation_id: nav.navigationId || nav.id || '',
        navigationList_name: navLabel,
      });
      if (String(nav.jumpType) === '1' && nav.jumpValue) {
        this.$router.push({ name: 'forum-assemble', params: { assembleId: nav.jumpValue }, query: { title: navLabel } });
      }
    },
    goSearch() {
      this.trackHomeButton('搜索');
      this.$router.push({ name: 'forum-search', query: { groupId: this.groupId } });
    },
    goMessages() {
      this.$router.push({ name: 'forum-messages' });
    },
    goCreate() {
      this.trackHomeButton('发帖');
      this.$router.push({
        name: 'forum-post-create',
        query: { groupId: this.groupId, groupName: this.detail.groupName, groupIcon: this.detail.groupIcon },
      });
    },
    async onPraise(post) {
      this.trackPostOpt(post, post.praise ? '取消点赞' : '点赞');
      const res = await praiseOrCancelPosts(post.postsId, post.praise ? 2 : 1).catch((err) => err);
      if (!toastInteraction(res, { successMsg: post.praise ? '已取消点赞' : '已点赞' })) return;
      post.praise = !post.praise;
      post.praiseNum = (post.praiseNum || 0) + (post.praise ? 1 : -1);
    },
    async onCollect(post) {
      this.trackPostOpt(post, post.collect ? '取消收藏' : '收藏');
      const res = await collectOrCancelPosts(post.postsId, post.collect ? 2 : 1).catch((err) => err);
      if (!isInteractionOk(res)) {
        toastInteraction(res);
        return;
      }
      post.collect = !post.collect;
      post.collectNum = (post.collectNum || 0) + (post.collect ? 1 : -1);
    },
    onComment(post) {
      this.trackPostOpt(post, '评论');
      this.markPostDetailReturn();
    },
    onPostClick(post) {
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_home_post_click, {
        ...this.getPostTrackingFields(post, '帖子列表'),
      });
      this.markPostDetailReturn();
    },
    onTopPostClick(post) {
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_home_post_click, {
        ...this.getPostTrackingFields(post, '置顶贴'),
      });
      this.markPostDetailReturn();
    },
    onTopPostToggle() {
      this.trackHomeButton('展开更多置顶');
    },
  },
};
</script>

<style scoped>
.forum_group {
  min-height: 100vh;
  padding-bottom: 160px;
  background: #f6f7f9;
}

.forum_group__cover {
  position: relative;
  box-sizing: border-box;
  height: 100px;
  min-height: 0;
  padding: 0 16px;
  background: linear-gradient(135deg, #6879ad, #303d66);
  background-size: cover;
  background-position: center;
}

.forum_group__message {
  position: absolute;
  top: 12px;
  right: 14px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 30px;
  padding: 0 10px;
  border: 0;
  border-radius: 15px;
  color: #fff;
  background: rgba(0, 0, 0, 0.28);
  font-size: 12px;
}

.forum_group__message svg {
  width: 16px;
  height: 16px;
  fill: currentColor;
}

.forum_group__message em {
  position: absolute;
  top: 2px;
  right: 5px;
  width: 7px;
  height: 7px;
  border: 1px solid #fff;
  border-radius: 50%;
  background: #fd383c;
}

.forum_group__info {
  position: absolute;
  right: 16px;
  bottom: 2px;
  left: 16px;
  display: flex;
  align-items: center;
  color: #fff;
}

.forum_group__icon {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 14px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.35);
}

.forum_group__icon img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.forum_group__text {
  min-width: 0;
  margin-left: 12px;
  overflow: hidden;
}

.forum_group__name {
  font-size: 18px;
  font-weight: 700;
}

.forum_group__desc {
  margin-top: 6px;
  font-size: 12px;
  opacity: 0.9;
}

.forum_group__bind_account {
  margin-top: 4px;
  font-size: 11px;
  line-height: 1.2;
  opacity: 0.92;
}

/* 与 common-tool-page GroupHome 导航+置顶白底区块对齐 */
.forum_group__top_block {
  width: 100%;
  box-sizing: border-box;
  margin-top: 8px;
  padding: 0 6px;
  background: #fff;
  border-radius: 12px 12px 0 0;
  overflow: hidden;
}

.forum_group__nav {
  display: flex;
  overflow-x: auto;
  padding-top: 12px;
  -webkit-overflow-scrolling: touch;
}

.forum_group__nav::-webkit-scrollbar {
  display: none;
}

.forum_group__nav_item {
  flex: 0 0 22.222%;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
  border: 0;
  padding: 0;
  background: transparent;
  color: #4b5563;
  font-size: 12px;
}

.forum_group__nav_icon {
  width: 42px;
  height: 42px;
  margin-bottom: 4px;
  border-radius: 8px;
  overflow: hidden;
  background: #f3f4f6;
}

.forum_group__nav_item img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.forum_group__nav_item span {
  width: 100%;
  text-align: center;
  font-size: 12px;
  line-height: 1;
}

.forum_group__filter {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.04);
}

.forum_group__primary,
.forum_group__secondary {
  display: flex;
  align-items: center;
  min-width: 0;
}

.forum_group__primary {
  padding: 10px 12px 0;
}

.forum_group__secondary {
  padding: 10px 12px;
  background: #fff;
}

.forum_group__primary_tabs,
.forum_group__secondary_tabs {
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1;
  overflow-x: auto;
  white-space: nowrap;
}

.forum_group__primary_tabs::-webkit-scrollbar,
.forum_group__secondary_tabs::-webkit-scrollbar {
  display: none;
}

.forum_group__primary_item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  border: 0;
  padding: 0;
  color: #777;
  background: transparent;
  font-size: 12px;
  line-height: 1;
}

.forum_group__primary_item + .forum_group__primary_item {
  margin-left: 16px;
}

.forum_group__primary_item.active {
  color: #111827;
  font-size: 16px;
  font-weight: 600;
}

.forum_group__primary_item i {
  width: 16px;
  height: 3px;
  margin-top: 4px;
  border-radius: 999px;
  background: transparent;
}

.forum_group__primary_item.active i {
  background: #ff5b5b;
}

.forum_group__search {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 66px;
  height: 22px;
  flex-shrink: 0;
  border: 0;
  margin-left: 8px;
  border-radius: 11px;
  padding: 0;
  color: #999;
  background: #ededed;
  font-size: 12px;
  line-height: 1;
}

.forum_group__search svg {
  width: 12px;
  height: 12px;
  margin-right: 3px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 2;
}

.forum_group__secondary_item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 0;
  border-radius: 5px;
  padding: 4px 8px;
  color: #636363;
  background: #ededed;
  font-size: 10px;
  line-height: 1;
}

.forum_group__secondary_item + .forum_group__secondary_item {
  margin-left: 8px;
}

.forum_group__secondary_item.active {
  color: #fff;
  background: #ea534f;
}

.forum_group__sort {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: 8px;
  color: #ccc;
  font-size: 12px;
  line-height: 1;
}

.forum_group__sort button {
  border: 0;
  padding: 0;
  color: #999;
  background: transparent;
  font-size: 12px;
  font-weight: 500;
}

.forum_group__sort span {
  margin: 0 2px;
}

.forum_group__sort .active {
  color: #0249ff;
  font-weight: 600;
}

.forum_group__create {
  position: fixed;
  right: 16px;
  bottom: 24px;
  width: 54px;
  height: 54px;
  border: 0;
  border-radius: 50%;
  color: #fff;
  background: #fd383c;
  font-size: 32px;
  line-height: 54px;
  box-shadow: 0 4px 12px rgba(253, 56, 60, 0.35);
}
</style>
